import React from 'react';
import { Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const FinancialSituation = ({ profile, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...profile, [field]: value });
  };

  return (
      <Row>
        {/* Pension Value */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="piggy-bank" />
            </div>
            <Form.Group className="mb-3">
              <label>What is your current pension value?</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={profile.pensionValue ?? ''}
                    onChange={(e) => handleChange('pensionValue', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('pensionValue', 0); }}
                    min={0}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Total value across all pension pots
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Annual Salary */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="wallet" />
            </div>
            <Form.Group className="mb-3">
              <label>What is your annual salary/income?</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={profile.annualSalary ?? ''}
                    onChange={(e) => handleChange('annualSalary', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('annualSalary', 0); }}
                    min={0}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Gross annual income before tax
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* NET TAKE HOME PAY - NEW FIELD */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="money-bill-wave" />
            </div>
            <Form.Group className="mb-3">
              <label>What is your monthly take home pay?</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={profile.netMonthlyIncome ?? ''}
                    onChange={(e) => handleChange('netMonthlyIncome', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('netMonthlyIncome', 0); }}
                    min={0}
                    placeholder="After tax and deductions"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Your actual monthly income after tax, NI, and any salary deductions
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Emergency Fund */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="shield-alt" />
            </div>
            <Form.Group className="mb-3">
              <label>Do you have an emergency fund?</label>
              <Form.Select
                  value={profile.emergencyFund}
                  onChange={(e) => handleChange('emergencyFund', e.target.value)}
              >
                <option value="no">No emergency fund</option>
                <option value="yes_less_3">Yes — less than 3 months' expenses</option>
                <option value="yes_3_months">Yes — around 3 months' expenses</option>
                <option value="yes_6_months">Yes — around 6 months' expenses</option>
                <option value="yes_12_months">Yes — 12+ months' expenses</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Savings to cover unexpected expenses (recommended: 3-6 months)
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Percentage of Wealth */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="chart-pie" />
            </div>
            <Form.Group className="mb-3">
              <label>What percentage of your total assets does this pension represent?</label>
              <Form.Select
                  value={profile.percentageOfWealth}
                  onChange={(e) => handleChange('percentageOfWealth', e.target.value)}
              >
                <option value="less_25">Less than 25%</option>
                <option value="25_to_50">25% to 50%</option>
                <option value="50_to_75">50% to 75%</option>
                <option value="more_75">More than 75%</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Excluding your home
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Current Debts */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon danger">
              <FontAwesomeIcon icon="credit-card" />
            </div>
            <Form.Group className="mb-3">
              <label>Which statement best describes your current debt situation?</label>
              <Form.Select
                  value={profile.debtLevel}
                  onChange={(e) => handleChange('debtLevel', e.target.value)}
              >
                <option value="none">I am completely debt free</option>
                <option value="no_mortgage_some_debt">No mortgage, but some credit cards/loans</option>
                <option value="mortgage_only">I have a mortgage but no other significant debts</option>
                <option value="mortgage_and_some">I have a mortgage and some short-term debts</option>
                <option value="significant">I have significant long-term debt obligations</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Col>

        {/* Future Earnings Expectation */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="chart-line" />
            </div>
            <Form.Group className="mb-3">
              <label>What is your expectation of your future earnings?</label>
              <Form.Select
                  value={profile.futureEarnings || 'keep_pace'}
                  onChange={(e) => handleChange('futureEarnings', e.target.value)}
              >
                <option value="decrease">I expect my earnings to decrease</option>
                <option value="keep_pace">I expect my earnings to keep pace with inflation</option>
                <option value="increase_somewhat">I expect my earnings to increase somewhat</option>
                <option value="increase_significantly">I expect my earnings to increase significantly</option>
                <option value="fluctuate">I expect my earnings to fluctuate</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Over the next 5 years
              </Form.Text>
            </Form.Group>
          </div>
        </Col>
      </Row>
  );
};

export default FinancialSituation;